import React from "react";

export const project3en = {
  name: "project3",
  title: `Urban biodiversity`,
  body: (
    <div>
      <div>
        <h3>Coordination</h3> Zdeňka Lososová
      </div>
      <div>
        <h3>Participants and collaborators</h3>
        omáš Čejka (SAV Bratislava), Natálie Čeplová, Jiří Danihelka, Pavel Dřevojan, Karel Fajmon (ČSOP Bílé Karpaty), Michal Horsák, Milan Chytrý, Lucie Juřičková (UK Praha), Veronika Kalníková, Veronika Kalusová, Kateřina Kintrová, Deana Láníková, Pavel Novák, Zdenka Preislerová, Vladimír Řehořek, Lubomír Tichý, Bohuslav Uher, Martin Večeřa.
Botanists, who participated in the mapping of the urban flora of Brno.

      </div>
	  <div><b>International cooperation</b></div>
<div>GUBIC consortium  <a href="https://cubes-labs.com/gubic/">(https://cubes-labs.com/gubic/)</a>

</div>
      <div>
        <h3>Funding</h3>
		<li>2025–2027: Czech Science Foundation, project no. 25-15190S (PI Zdeňka Lososová)</li>
<li>2022–2024: sDIV, project named: Synthesizing Global Urban Biological Invasion Knowledge (sGUBIK): Patterns, mechanisms and applications (PI Luke Potgieter)</li> 
<li>2014–2016: Czech Science Foundation, project no. 14-10723S (PI Zdeňka Lososová)</li>
<li>2008–2010: Grant Agency of the Academy of Sciences of the Czech Republic, project no. IAA601630803 (PI Zdeňka Lososová)</li>
      </div>
      <div>
        <h3>Aims</h3>
        <div>
          We have been studying the urban biodiversity of large European cities for a long time. We work across different spatial scales. Our studies focus on detailed knowledge of diversity within a single city, and we further verify the results at a continental scale or by comparing data among cities worldwide.
        </div>
		
		<div>Within the city of Brno, we obtained detailed data on the city’s flora. These allow us to identify local centres of plant diversity in the urban environment and to determine the factors that lead to their formation.</div>
		<div>At the European scale, we worked using standardized data-collection protocols. In selected anthropogenic habitats and in selected cities representing various macroclimatic and socioeconomic regions of Europe, we primarily studied the diversity of vascular plants. However, we also collected data on the occurrence of terrestrial molluscs and epilithic and epixylic terrestrial algae and cyanobacteria. On these groups, and across different spatial scales, we address four main research topics:</div>

		
        <li>To identify emerging urban species communities and their functions.</li>
        <li>
          To compare urbanization effect on biota across different spatial scales.
        </li>
        <li>
          o test factors driving functional and phylogenetic structure in different urban habitats and between different climatic regions of Europe.
        </li>
        <li>To study invasibility of urban biota</li>
      </div>
	  
      <div className="main-results">
        <div>
          <h3>Main results</h3>
          <div>
            <h4>
            Flora of Brno 
            </h4>
          </div>
          <div>Which plant species are able to occur spontaneously within the city of Brno is documented in a study by Lososová et al. (2024). Between 2011 and 2021, our team systematically mapped spontaneous occurrences of vascular plants in Brno using a grid of mapping squares measuring 1.3 × 1.5 km. In this way, we recorded 1,492 spontaneously occurring vascular plant species in the city. Their distribution is documented in individual maps.</div>
<div>Although urbanization leads to the destruction of natural habitats and the disappearance of a considerable number of native species, detailed floristic research has shown that a relatively high number of plant taxa survive in the urban environment. This is because, alongside native plant species that thrive in certain urban habitats, cities also harbor a large number of non-native species. These were brought into the urban environment by humans—either intentionally as ornamental plants that later escaped cultivation and began to spread, or unintentionally as weeds or various plant contaminants most often introduced through transport.</div>
<div>The results of the study show that native species dominate in the least urbanized areas, especially in the forested parts of the city in the west and north. In contrast, non-native species are more frequent in the built-up city center and in the flat, treeless southern part of Brno. The most common species growing in the city are also widespread elsewhere in the Czech Republic. In addition to these common species, the occurrence of 255 threatened plants is noteworthy.</div>
<div>Although urban lawns and parks are important for residents, they have only limited significance for spontaneous flora due to very early and frequent mowing and the predominance of ornamental plantings. For the species richness of the urban flora, the diversity of the urban environment is crucial, including preserved remnants of semi-natural forest and grassland habitats on the city’s periphery.</div>
<div>Although forests are generally the least threatened by the spread of non-native species within the urban environment, the degree of disturbance of their herb layer increases with the intensity of anthropogenic influence on vegetation (Kadaš & Lososová 2025).</div>

        </div>
        <div>
          <h4>Biodiversity of European Urban Vegetation</h4>
		  By comparing the species richness of seven typical urban habitats in major European cities, we showed that while cities in the driest and warmest regions are species-poor and contain a smaller proportion of freely spreading non-native species, cities in cooler and more humid parts of Europe are richer in species but also more prone to invasions by non-native species (Lososová et al. 2011; Lososová et al. 2012a,b; Chytrý et al. 2012; Čeplová et al. 2015; Kalusová et al. 2019). Similar trends have been demonstrated for terrestrial molluscs (Lososová et al. 2011; Horsák et al. 2013, 2016). Using species traits, we identified functional types of plants that succeed in the urban environment (Lososová et al. 2016; Kalusová et al. 2017; Čeplová et al. 2017). With increasing intensity of urbanization, the functional specialization of plants increases (Di Guilio et al. 2024). Based on our data, we can predict how the flora of urban environments is likely to change with ongoing climate change (Lososová et al. 2019).

        </div>
        <div>
          <h4>Global Trends in Urban Plant Diversity</h4>
		  Similarly to European cities, Tatyana Vakhlamova collected information on species richness in selected urban habitats in Kazakhstan and found that the same factors influencing the flora of European cities are equally important for shaping the flora of Kazakh cities (Vakhlamova et al. 2022).
Thanks to collaboration with the international  <a href="https://cubes-labs.com/gubic/">GUBIC team</a>, we contributed to building a global database of non-native plants occurring in 553 cities worldwide (Li et al. 2025). We assess how cities influence plant diversity on a global scale (Potgieter et al. 2024). By analyzing our database, we identified which non-native species are most common in urban environments. We demonstrated that all continents except Oceania are significant donors of non-native species to cities (Richardson et al. 2025).

        </div>
      </div>
      <div className="project-publication">
        <h3>Publications</h3>
        <h4>Research papers</h4>
		<li><b>Kadaš D.</b> & <b>Lososová Z.</b> (2025): Drivers of plant species diversity in suburban forests: A case study from the Czech Republic. <i>Basic and Applied Ecology</i> 88: 83–93. – {" "}<a href="https://doi.org/10.1016/j.baae.2025.08.007">DOI</a>
		</li>
		
		<li>Li D., Potgieter L.J., Aronson M.F.J., <b>Axmanová I.</b>, Baiser B., Carboni M., Celesti-Grapow L., Knapp S., Kühn I., de Matos A.C.L., <b>Lososová Z.</b>, Montaño-Centellas F.A., Pyšek P., Richardson D.M., Trotta L.B., Zenni R.D., Cilliers S.S., Clarkson B.D., Davis A.J.S., Dolan R.W., Dyderski M.K., Essl F., Gaoue O.G., Gui J., Géron C., Heringer G., Hui C., Khuroo A.A., Klotz S., Kotanen P.M., Kreft H., La Sorte F.A., Lembrechts J.J., Lenzner B., Lepczyk C.A., MacIvor S., Martínez-Garza C., Mori A.S., Nilon C., Pergl J., Siebert S.J., Tretyakova A.S., Tsang T.P.N., Uchida K., van Kleunen M., Vilà M., Wang H.-F., Weigelt P., Werner P., Williams N.S.G., Winter M. & Cadotte M.W. (2025): GUBIC: The global urban biological invasions compendium for plants. <i>Ecological Solutions and Evidence</i> 6: e70020. – {" "}<a href="https://doi.org/10.1002/2688-8319.70020">DOI</a>
		</li>
		
		<li>
		Di Giulio M., <b>Lososová Z.</b>, Carboni M. & Ricotta C. (2024): Measuring plant functional specialization in urban environments with Grime´s CSR strategies. <i>Applied Vegetation Science</i> 27: e12803. – {" "}<a href="https://doi.org/10.1111/avsc.12803">DOI</a>
		</li>
		
		<li><b>Lososová Z., Danihelka J., Dřevojan P., Hájek O., Kalusová V., Večeřa M., Chytrý K., Chytrý M., Čeplová N.</b>, Filippov P., <b>Jiroušek M.</b>, <b>Kadaš D.</b>, Kalníková V., <b>Knollová I.</b>, Macků M., Niederle J., <b>Novák P., Rohel J., Rotreklová O.</b>, Řepka R., <b>Řezníčková M., Šmerdová E., Šumberová K., Veselý P.</b>, Vymazalová M., Wirth T. & <b>Tichý L.</b> (2024): Flora of the city of Brno, Czech Republic. <i>Preslia</i> 96: 123–147. – {" "}<a href="https://doi.org/10.23855/preslia.2024.123">DOI</a>
		
		</li>
		
		<li>Potgieter L.J., Li D., Baiser B., Kühn I., Aronson M.F.J., Carboni M., Celesti-Grapow L., de Matos A.C.L., <b>Lososová Z.</b>, Montaño-Centellas F.A., Pyšek P., Richardson D.M., Tsang T.P.N., Zenni R.D. & Cadotte M.W. (2024): Cities Shape the Diversity and Spread of Nonnative Species. <i>Annual Review of Ecology, Evolution, and Systematics</i> 55: 157–180. – 
		{" "}<a href="https://doi.org/10.1146/annurev-ecolsys-102722-012749">DOI</a>
		</li>
		
		<li><b>Vakhlamova T.</b>, Wagner V., <b>Padullés Cubino J., Chytrý M.</b> & <b>Lososová Z.</b> (2022): Urban plant diversity in Kazakhstan: Effects of habitat type, city size and macroclimate. <i>Applied Vegetation Science</i> 25: e12679. – 
		{" "}<a href="https://doi.org/10.1111/avsc.12679">DOI</a>
		</li>
		<li>Swan C. M., Brown B., Borowy D., Cavender-Bares J., Jeliazkov A., Knapp S., <b>Lososová Z.</b>, <b>Cubino J. P.</b>, Pavoine S., Ricotta C. & Sol D. (2021): A framework for understanding how biodiversity patterns unfold across
multiple spatial scales in urban ecosystems. <i>Ecosphere</i> 12:e03650.  – 
{" "}<a href="https://doi.org/10.1002/ecs2.3650">DOI</a>
</li>
        <li><b>Cubino J. P.</b>, Borowy D., Knapp S., <b>Lososová Z.</b>, Ricotta C., Siebert S., Cavender-Bares J., Sol D., Jeliazkov A. & Swan C. (2020): Contrasting Impacts of Cultivated Exotics on the Functional Diversity of Domestic Gardens in Three Regions with Different Aridity. <i>Ecosystems</i>.  – 
{" "}<a href="https://doi.org/10.1007/s10021-020-00556-x">DOI</a>
</li>
<li>
<b>Kalusová V.</b>, <b>Čeplová N.</b>, <b>Chytrý M.</b>, <b>Danihelka J.</b>, <b>Dřevojan P.</b>, Fajmon K., <b>Hájek O.</b>, <b>Kalníková V.</b>, <b>Novák P.</b>, <b>Řehořek V.</b>, <b>Těšitel J.</b>, <b>Tichý L.</b>, Wirth T. & <b>Lososová Z.</b> (2019): Similar responses of native and alien floras in European cities to climate. <i>Journal of Biogeography</i> 46: 1406–1418. –
 {" "}<a href="https://onlinelibrary.wiley.com/doi/abs/10.1111/jbi.13591">fulltext in Wiley Online Library</a>
</li>
         <li>
          <b>Lososová Z.</b>, <b>Tichý L.</b>, <b>Divíšek J.</b>, <b>Čeplová N.</b>, <b>Danihelka J.</b>, <b>Dřevojan P.</b>, Fajmon K.,
          <b> Kalníková V.</b>, <b>Kalusová V.</b>, <b>Novák P.</b>, <b>Řehořek V.</b>, Wirth T. &amp; <b>Chytrý M.</b> (2018):
          Projecting potential future shifts in species composition of European urban plant
          communities. <i>Diversity and Distributions</i> 24: 765–775. –
		  {" "}<a href="https://onlinelibrary.wiley.com/doi/epdf/10.1111/ddi.12725">fulltext in Wiley Online Library</a>
        </li>
<li>
          <b>Kalusová V.</b>, <b>Čeplová N.</b> &amp; <b>Lososová Z.</b> (2017): Which traits influence the frequency of
          plant species occurrence in urban habitat types? <i>Urban Ecosystems</i> 20: 65–75. –
		   {" "}<a href="https://www.researchgate.net/publication/306087693_Which_traits_influence_the_frequency_of_plant_species_occurrence_in_urban_habitat_types">fulltext on ResearchGate</a>
        </li>
        <li>
          <b>Čeplová N.</b>, <b>Kalusová V.</b> &amp; <b>Lososová Z.</b> (2017): Effects of settlement size, urban heat
          island and habitat type on urban plant biodiversity. <i>Landscape and Urban Planning</i> 159:
          15–22.
        </li>
        <li>
          <b>Čeplová N.</b>, <b>Lososová Z.</b> &amp; <b>Kalusová V.</b> (2017): Urban ornamental trees: a source of
          current invaders; a case study from a European City. <i>Urban Ecosystems</i> 20: 1135–1140. – 
           {" "}<a href="https://link.springer.com/content/pdf/10.1007%2Fs11252-017-0665-2.pdf">fulltext on SpringerLink</a>
        </li>
        <li>
          <b>Horsák M.</b>, Čejka T., Juřičková L., Wiese V., <b>Horsáková L.</b> &amp; <b>Lososová Z.</b> (2016):
          Drivers of Central European urban land snail faunas: the role of climate and local species
          pool in the representation of native and non-native species. <i>Biological Invasions</i> 18:
          3547–3560.
        </li>
<li>
          <b>Lososová Z.</b>, <b>Chytrý M.</b>, <b>Danihelka J.</b>, <b>Tichý L.</b> &amp; Ricotta C. (2016a): Biotic
          homogenization of urban floras by alien species: the role of species turnover and richness
          differences. <i>Journal of Vegetation Science</i> 27: 452–459.
        </li>
        <li>
          <b>Lososová Z.</b>, <b>Čeplová N.</b>, <b>Chytrý M.</b>, <b>Tichý L.</b>, <b>Danihelka J.</b>, Fajmon K., <b>Láníková D.</b>,
          <b> Preislerová Z.</b> &amp; <b>Řehořek V.</b> (2016b): Is phylogenetic diversity a good proxy for
          functional diversity of plant communities? A case study from urban habitats. <i>Journal of
          Vegetation Science</i> 27: 1036–1046.
        </li>
<li>
          <b>Čeplová N.</b>, <b>Lososová Z.</b>, <b>Zelený D.</b>, <b>Chytrý M.</b>, <b>Danihelka J.</b>, Fajmon K., <b>Láníková D.</b>, 
          <b> Preislerová Z.</b>, <b>Řehořek V.</b> &amp; <b>Tichý L.</b> (2015): Phylogenetic diversity of central-
          European urban plant communities: effects of alien species and habitat types. <i>Preslia</i> 87:
          1–16.
        </li>
        <li>
          <b>Horsák M.</b>, <b>Lososová Z.</b>, Čejka T., Juřičková L. &amp; <b>Chytrý M.</b> (2013): Diversity and
          biotic homogenization of urban land-snail faunas in relation to habitat types and
          macroclimate in 32 Central European cities. <i>PLoS ONE</i> 8: e71783. – 
		  {" "}<a href="http://www.sci.muni.cz/botany/chytry/Horsak_etal2013_PLOS-ONE.pdf">fulltext in pdf</a>
        </li>
        <li>
          <b>Chytrý M.</b>, <b>Lososová Z.</b>, <b>Horsák M.</b>, Uher B., Čejka T., <b>Danihelka J.</b>, Fajmon K., <b>Hájek O.</b>,
          Juřičková L., <b>Kintrová K.</b>, <b>Láníková D.</b>, <b>Otýpková Z.</b>, <b>Řehořek V.</b> &amp; <b>Tichý L.</b> (2012):
          Dispersal limitation is stronger in communities of microorganisms than macroorganisms
          across Central European cities. <i>Journal of Biogeography</i> 39: 1101–1111. – 
		  {" "}<a href="http://onlinelibrary.wiley.com/doi/10.1111/j.1365-2699.2011.02664.x/abstract">fulltext in Wiley Online Library</a>
        </li>
        
        <li>
          <b>Lososová Z.</b>, <b>Chytrý M.</b>, <b>Tichý L.</b>, <b>Danihelka J.</b>, Fajmon K., <b>Hájek O.</b>, <b>Kintrová K.</b>, Kühn I.,
          <b> Láníková D.</b>, <b>Otýpková Z.</b> &amp; <b>Řehořek V.</b> (2012): Native and alien floras in urban
          habitats: a comparison across 32 cities of central Europe. <i>Global Ecology and Biogeography </i>
          21: 545–555. – 
		  {" "}<a href="http://onlinelibrary.wiley.com/doi/10.1111/j.1466-8238.2011.00704.x/abstract">fulltext in Wiley Online Library</a>
        </li>
        <li>
          <b>Lososová Z.</b>, <b>Chytrý M.</b>, <b>Tichý L.</b>, <b>Danihelka J.</b>, Fajmon K., <b>Hájek O.</b>, <b>Kintrová K.</b>, Láníková
          D., <b>Otýpková Z.</b> &amp; <b>Řehořek V.</b> (2012): Biotic homogenization of Central European urban
          floras depends on residence time of alien species and habitat types. <i>Biological
          Conservation</i> 145: 179–184.
        </li>
        <h4>Dissertation</h4>
        <li>
          <b>Čeplová N.</b> (2016): Diversity of European urban vegetation. Thesis. Ms. Masaryk University,
          Brno.
        </li>
        <h4>Popularizations</h4>
         <li>
          <b>Lososová Z.</b> (2018): Diverzita rostlinných společenstev evropských měst. <i>Zprávy České
          botanické společnosti</i> 53: 207–216.
        </li>
<li>
          Pergl J., <b>Lososová Z.</b>, Sádlo J. &amp; Štajerová K. (2018): Rostlinné invaze na
          antropogenních stanovištích. <i>Živa</i> 5: 233–234.
        </li>   
 <li>
 <b>Lososová Z.</b>, Kovář P., Medvecká J., Mudrák O., Pergl J. & Prach K. (2018): Úvod: Flóra a vegetace antropogenních stanovišť. <i>Zprávy České botanické společnosti</i> 53: 199–205.
</li>

<li>
          <b>Čeplová N.</b> &amp; <b>Kalusová V.</b> (2016): Jak velikost města ovlivňuje druhové složení
          vegetace? <i>Fórum ochrany přírody</i> 3/4: 30–34.
        </li>
       <li>
          <b>Lososová Z.</b>, Šumberová K., <b>Tichý L.</b>, <b>Vymazalová M.</b> &amp; <b>Danihelka J.</b> (2015): Květena Brna
          – současný stav poznání. <i>Živa</i> 2015/6: 289–292.
        </li>
        <li>
          <b>Lososová Z.</b> &amp; <b>Danihelka J.</b> (2014): Proč je flóra velkoměst pestrá. <i>Živa</i> 2014/6:
          278–281.
        </li>
      </div>
    </div>
  )
};
export const project3cz = {
  name: "project3",
  title: `Biodiverzita měst`,
  body: (
        <div>
      <div>
        <h3>Koordinátor</h3> Zdeňka Lososová
      </div>
      <div>
        <h3>Spolupracovníci</h3> Tomáš Čejka (SAV Bratislava), Natálie Čeplová, Jiří Danihelka, Karel
        Fajmon (ČSOP Bílé Karpaty), Michal Horsák, Milan Chytrý, Lucie Juřičková (UK Praha),
        Veronika Kalusová, Deana Láníková, Zdenka Preislerová, Vladimír Řehořek, Lubomír Tichý,
        Bohuslav Uher, Martin Večeřa
      </div>
	   <div>{" "}
	   </div>
<div><b>Další spolupracovníci</b></div>
<div>Kolegové podílející se na mapování květeny města Brna</div>
<div><b>Mezinárodní spolupráce</b></div>
<div>konsorcium GUBIC <a href="https://cubes-labs.com/gubic/">(https://cubes-labs.com/gubic/)</a>

</div>
      <div>
        <h3>Finanční podpora</h3>
		<li>2025–2027: Grantová agentura České republiky, projekt č. 25-15190S (řešitelka Zdeňka Lososová)</li>
		<li>2022–2024: sDIV, project named: Synthesizing Global Urban Biological Invasion Knowledge (sGUBIK): Patterns, mechanisms and applications (PI Luke Potgieter)</li> 
         <li>2014–2016: Grantová agentura České republiky, projekt č. 14-10723S (řešitelka: Zdeňka Lososová)</li>
         <li>2008–2010: Grantová agentura Akademie věd České republiky, projekt č. IAA601630803 (řešitelka: Zdeňka Lososová)</li>
      </div>
      <div>
        <h3>Cíle</h3>
        <div>
          Dlouhodobě se zabýváme studiem městské biodiverzity. Pracujeme na různých prostorových škálách. Naše studie se soustředí na detailní znalosti diverzity v rámci jednoho města, výsledky dále ověřujeme na kontinentální škále případně srovnáním dat mezi městy celosvětově.
		  </div>

<div>V rámci města Brna jsme získali podrobná data o květeně města. Ta nám umožní stanovit lokální centra diverzity rostlin v městském prostředí a zjistit faktory, které vedou k jejich vzniku. </div>
<div>Na evropské škále jsme pracovali pomocí standardizovaných protokolů sběru dat. Ve vybraných antropogenních biotopech a vybraných městech, reprezentujících různé makroklimatické a socioekonomické oblasti Evropy jsme studovali především diverzitu cévnatých rostlin. Získali jsme však také údaje o výskytu terestrických plžů a epilitických a epixylických terestrických řas a sinic. Na těchto organismech a na rozdílných prostorových škálách řešíme čtyři hlavní okruhy otázek</div>

<li>
Jaké faktory ovlivňují šíření nepůvodních druhů v městském prostředí?
</li>
<li>
Působí městské prostředí negativně nebo pozitivně na diverzitu původních druhů a na celkovou druhovou bohatost vybraných taxonomických skupin?
</li>
<li>
Způsobuje urbanizace biotickou homogenizaci městských biotopů, a pokud ano, projevuje se homogenizace na úrovni druhů i na úrovni druhových vlastností? Které biotopy a které skupiny druhů jsou homogenizací nejvíce postiženy?
</li>
<li>
Jsou trendy v diverzitě městských biotopů konzistentní v různých taxonomických skupinách?
</li>
</div>

        
	  <div className="main-results">
	  <div>
        <h3>Hlavní výsledky</h3>
		<div>
<h4>Květena města Brna</h4>
</div>
<div>
<div>Které rostliny jsou schopné spontánně se vyskytovat na území města Brna dokumentuje studie (Lososová et al. 2024). V letech 2011–2021 náš tým systematicky mapoval spontánní výskyty cévnatých rostlin v Brně, a to v síti mapovacích polí o rozměrech 1,3 x 1,5 km. Tímto způsobem zaznamenali ve městě výskyt 1492 spontánně se vyskytujících cévnatých rostlin. Jejich rozšíření je dokumentováno jednotlivými mapami.</div>
<div>Přestože urbanizace vede k destrukci přirozených stanovišť a k mizení nemalého počtu původních druhů, detailní floristický výzkum ukázal, že v městském prostředí přežívá relativně vysoký počet rostlinných taxonů. Je to dáno tím, že vedle původních druhů rostlin, kterým vyhovují některé městské biotopy, se ve městech nachází také velké množství nepůvodních druhů. Ty jsme do městského prostředí přinesly my lidé ať už úmyslně jako okrasné rostliny, které posléze zplaněly a začaly se šířit, nebo neúmyslně jako plevele nebo různé rostlinné příměsi zavlečené nejčastěji s dopravou.</div>
<div>Výsledky studie dokumentují, že původní druhy převažují v nejméně urbanizovaných územích, především v lesnatých částech v západní a severní části města. Naopak nepůvodní druhy jsou četnější v zastavěném centru a v jižní rovinaté bezlesé části města. Nejběžnější druhy rostoucí ve městě, jsou hojné i jinde v České republice. Kromě těchto běžných druhů je zajímavý výskyt 255 ohrožených rostlin.</div>
<div>Městské trávníky a parky jakkoli důležité pro život obyvatel, mají jen omezený význam pro spontánní flóru v důsledku velmi časné a časté seče a převahy okrasných výsadek. Pro druhovou bohatost městské flóry je důležitá především různorodost městského prostředí včetně zachovalých zbytků polopřirozených lesních a travnatých biotopů na městské periferii.</div>
<div>Přestože jsou lesy obecně nejméně ohrožené šířením nepůvodních druhů v rámci městského prostředí. Míra narušení jejich bylinného podrostu stoupá s intenzitou antropogenního vlivu na vegetaci (Kadaš & Lososová 2025).</div>

</div>
		
        <div>
		<h4>Biodiverzita evropských měst
		</h4>
</div>
<div>
Srovnáním druhové bohatosti sedmi typických městských stanovišť v evropských velkoměstech jsme ukázali, že zatímco města v nejsušších a nejteplejších oblastech jsou druhově chudá a obsahují i menší podíl nepůvodních volně se šířících druhů, města v chladnějších a humidnějších částech Evropy jsou druhově bohatší, avšak také náchylnější k invazi nepůvodních druhů (Lososová et al. 2011, Lososová et al. 2012a,b, Chytrý et al. 2012; Čeplová et al. 2015; Kalusová et al. 2019). Podobné trendy byly prokázány u suchozemských plžů (Lososová et al. 2011, Horsák et al. 2013, 2016). Pomocí vlastností druhů jsme ukázali funkční typy rostlin, které jsou ve městském prostředí úspěšné (Lososová et al. 2016; Kalusová et al. 2017, Čeplová et al. 2017). S rostoucí intenzitou urbanizace roste funkční specializace rostlin (Di Guilio et al. 2024). Na základě našich data můžeme predikovat, jak se bude pravděpodobně měnit květena městského prostředí s postupnou změnou klimatu (Lososová et al. 2019).
 
</div>

<div>
		<h4>Globální trendy v městské diverzitě rostlin
		</h4>
</div>

Podobně jako v evropských městech zapsala informace o druhové bohatosti vybraných stanovišť ve městech Kazachstánu Tatyana Vakhlamova, zjistila, že stejné faktory, které ovlivňují květenu měst v Evropě, jsou stejně tak důležité pro utváření květeny měst Kazachstánu (Vakhlamova et al. 2022).
Díky spolupráci s mezinárodním týmem <a href="https://cubes-labs.com/gubic/">GUBIC</a> jsme se podíleli na sestavení celosvětové databáze nepůvodních rostlin vyskytujících se v 553 městech světa (Li et al. 2025). Zjišťujeme, jak města ovlivňují diverzitu rostlin na globální škále (Potgieter et al. 2024). Analýzou těchto dat jsme ukázali, které nepůvodní druhy jsou v městském prostředí nejčastější. Prokázali jsme, že všechny kontinenty s výjimkou Oceánie jsou významní donoři nepůvodních druhů měst (Richardson et al. 2025).
 
</div>

</div>

      <div className="project-publication">
        <h3>Publikace</h3>
        <h4>Odborné články</h4>
		<li><b>Kadaš D.</b> & <b>Lososová Z.</b> (2025): Drivers of plant species diversity in suburban forests: A case study from the Czech Republic. <i>Basic and Applied Ecology</i> 88: 83–93. – {" "}<a href="https://doi.org/10.1016/j.baae.2025.08.007">DOI</a>
		</li>
		
		<li>Li D., Potgieter L.J., Aronson M.F.J., <b>Axmanová I.</b>, Baiser B., Carboni M., Celesti-Grapow L., Knapp S., Kühn I., de Matos A.C.L., <b>Lososová Z.</b>, Montaño-Centellas F.A., Pyšek P., Richardson D.M., Trotta L.B., Zenni R.D., Cilliers S.S., Clarkson B.D., Davis A.J.S., Dolan R.W., Dyderski M.K., Essl F., Gaoue O.G., Gui J., Géron C., Heringer G., Hui C., Khuroo A.A., Klotz S., Kotanen P.M., Kreft H., La Sorte F.A., Lembrechts J.J., Lenzner B., Lepczyk C.A., MacIvor S., Martínez-Garza C., Mori A.S., Nilon C., Pergl J., Siebert S.J., Tretyakova A.S., Tsang T.P.N., Uchida K., van Kleunen M., Vilà M., Wang H.-F., Weigelt P., Werner P., Williams N.S.G., Winter M. & Cadotte M.W. (2025): GUBIC: The global urban biological invasions compendium for plants. <i>Ecological Solutions and Evidence</i> 6: e70020. – {" "}<a href="https://doi.org/10.1002/2688-8319.70020">DOI</a>
		</li>
		
		<li>
		Di Giulio M., <b>Lososová Z.</b>, Carboni M. & Ricotta C. (2024): Measuring plant functional specialization in urban environments with Grime´s CSR strategies. <i>Applied Vegetation Science</i> 27: e12803. – {" "}<a href="https://doi.org/10.1111/avsc.12803">DOI</a>
		</li>
		
		<li><b>Lososová Z., Danihelka J., Dřevojan P., Hájek O., Kalusová V., Večeřa M., Chytrý K., Chytrý M., Čeplová N.</b>, Filippov P., <b>Jiroušek M.</b>, <b>Kadaš D.</b>, Kalníková V., <b>Knollová I.</b>, Macků M., Niederle J., <b>Novák P., Rohel J., Rotreklová O.</b>, Řepka R., <b>Řezníčková M., Šmerdová E., Šumberová K., Veselý P.</b>, Vymazalová M., Wirth T. & <b>Tichý L.</b> (2024): Flora of the city of Brno, Czech Republic. <i>Preslia</i> 96: 123–147. – {" "}<a href="https://doi.org/10.23855/preslia.2024.123">DOI</a>
		
		</li>
		
		<li>Potgieter L.J., Li D., Baiser B., Kühn I., Aronson M.F.J., Carboni M., Celesti-Grapow L., de Matos A.C.L., <b>Lososová Z.</b>, Montaño-Centellas F.A., Pyšek P., Richardson D.M., Tsang T.P.N., Zenni R.D. & Cadotte M.W. (2024): Cities Shape the Diversity and Spread of Nonnative Species. <i>Annual Review of Ecology, Evolution, and Systematics</i> 55: 157–180. – 
		{" "}<a href="https://doi.org/10.1146/annurev-ecolsys-102722-012749">DOI</a>
		</li>
		
		<li><b>Vakhlamova T.</b>, Wagner V., <b>Padullés Cubino J., Chytrý M.</b> & <b>Lososová Z.</b> (2022): Urban plant diversity in Kazakhstan: Effects of habitat type, city size and macroclimate. <i>Applied Vegetation Science</i> 25: e12679. – 
		{" "}<a href="https://doi.org/10.1111/avsc.12679">DOI</a>
		</li>
		
		<li>Swan C. M., Brown B., Borowy D., Cavender-Bares J., Jeliazkov A., Knapp S., <b>Lososová Z.</b>, <b>Cubino J. P.</b>, Pavoine S., Ricotta C. & Sol D. (2021): A framework for understanding how biodiversity patterns unfold across
multiple spatial scales in urban ecosystems. <i>Ecosphere</i> 12:e03650.  – 
{" "}<a href="https://doi.org/10.1002/ecs2.3650">DOI</a>
</li>
		<li><b>Cubino J. P.</b>, Borowy D., Knapp S., <b>Lososová Z.</b>, Ricotta C., Siebert S., Cavender-Bares J., Sol D., Jeliazkov A. & Swan C. (2020): Contrasting Impacts of Cultivated Exotics on the Functional Diversity of Domestic Gardens in Three Regions with Different Aridity. <i>Ecosystems</i>.  – 
{" "}<a href="https://doi.org/10.1007/s10021-020-00556-x">DOI</a>
</li>
<li>
<b>Kalusová V.</b>, <b>Čeplová N.</b>, <b>Chytrý M.</b>, <b>Danihelka J.</b>, <b>Dřevojan P.</b>, Fajmon K., <b>Hájek O.</b>, <b>Kalníková V.</b>, <b>Novák P.</b>, <b>Řehořek V.</b>, <b>Těšitel J.</b>, <b>Tichý L.</b>, Wirth T. & <b>Lososová Z.</b> (2019): Similar responses of native and alien floras in European cities to climate. <i>Journal of Biogeography</i> 46: 1406–1418. –
 {" "}<a href="https://onlinelibrary.wiley.com/doi/abs/10.1111/jbi.13591">fulltext in Wiley Online Library</a>
</li>
         <li>
          <b>Lososová Z.</b>, <b>Tichý L.</b>, <b>Divíšek J.</b>, <b>Čeplová N.</b>, <b>Danihelka J.</b>, <b>Dřevojan P.</b>, Fajmon K.,
          <b> Kalníková V.</b>, <b>Kalusová V.</b>, <b>Novák P.</b>, <b>Řehořek V.</b>, Wirth T. &amp; <b>Chytrý M.</b> (2018):
          Projecting potential future shifts in species composition of European urban plant
          communities. <i>Diversity and Distributions</i> 24: 765–775. –
		  {" "}<a href="https://onlinelibrary.wiley.com/doi/epdf/10.1111/ddi.12725">fulltext in Wiley Online Library</a>
        </li>
<li>
          <b>Kalusová V.</b>, <b>Čeplová N.</b> &amp; <b>Lososová Z.</b> (2017): Which traits influence the frequency of
          plant species occurrence in urban habitat types? <i>Urban Ecosystems</i> 20: 65–75. –
		   {" "}<a href="https://www.researchgate.net/publication/306087693_Which_traits_influence_the_frequency_of_plant_species_occurrence_in_urban_habitat_types">fulltext on ResearchGate</a>
        </li>
        <li>
          <b>Čeplová N.</b>, <b>Kalusová V.</b> &amp; <b>Lososová Z.</b> (2017): Effects of settlement size, urban heat
          island and habitat type on urban plant biodiversity. <i>Landscape and Urban Planning</i> 159:
          15–22.
        </li>
        <li>
          <b>Čeplová N.</b>, <b>Lososová Z.</b> &amp; <b>Kalusová V.</b> (2017): Urban ornamental trees: a source of
          current invaders; a case study from a European City. <i>Urban Ecosystems</i> 20: 1135–1140. – 
           {" "}<a href="https://link.springer.com/content/pdf/10.1007%2Fs11252-017-0665-2.pdf">fulltext on SpringerLink</a>
        </li>
        <li>
          <b>Horsák M.</b>, Čejka T., Juřičková L., Wiese V., <b>Horsáková L.</b> &amp; <b>Lososová Z.</b> (2016):
          Drivers of Central European urban land snail faunas: the role of climate and local species
          pool in the representation of native and non-native species. <i>Biological Invasions</i> 18:
          3547–3560.
        </li>
<li>
          <b>Lososová Z.</b>, <b>Chytrý M.</b>, <b>Danihelka J.</b>, <b>Tichý L.</b> &amp; Ricotta C. (2016a): Biotic
          homogenization of urban floras by alien species: the role of species turnover and richness
          differences. <i>Journal of Vegetation Science</i> 27: 452–459.
        </li>
        <li>
          <b>Lososová Z.</b>, <b>Čeplová N.</b>, <b>Chytrý M.</b>, <b>Tichý L.</b>, <b>Danihelka J.</b>, Fajmon K., <b>Láníková D.</b>,
          <b> Preislerová Z.</b> &amp; <b>Řehořek V.</b> (2016b): Is phylogenetic diversity a good proxy for
          functional diversity of plant communities? A case study from urban habitats. <i>Journal of
          Vegetation Science</i> 27: 1036–1046.
        </li>
<li>
          <b>Čeplová N.</b>, <b>Lososová Z.</b>, <b>Zelený D.</b>, <b>Chytrý M.</b>, <b>Danihelka J.</b>, Fajmon K., <b>Láníková D.</b>, 
          <b> Preislerová Z.</b>, <b>Řehořek V.</b> &amp; <b>Tichý L.</b> (2015): Phylogenetic diversity of central-
          European urban plant communities: effects of alien species and habitat types. <i>Preslia</i> 87:
          1–16.
        </li>
        <li>
          <b>Horsák M.</b>, <b>Lososová Z.</b>, Čejka T., Juřičková L. &amp; <b>Chytrý M.</b> (2013): Diversity and
          biotic homogenization of urban land-snail faunas in relation to habitat types and
          macroclimate in 32 Central European cities. <i>PLoS ONE</i> 8: e71783. – 
		  {" "}<a href="http://www.sci.muni.cz/botany/chytry/Horsak_etal2013_PLOS-ONE.pdf">fulltext in pdf</a>
        </li>
        <li>
          <b>Chytrý M.</b>, <b>Lososová Z.</b>, <b>Horsák M.</b>, Uher B., Čejka T., <b>Danihelka J.</b>, Fajmon K., <b>Hájek O.</b>,
          Juřičková L., <b>Kintrová K.</b>, <b>Láníková D.</b>, <b>Otýpková Z.</b>, <b>Řehořek V.</b> &amp; <b>Tichý L.</b> (2012):
          Dispersal limitation is stronger in communities of microorganisms than macroorganisms
          across Central European cities. <i>Journal of Biogeography</i> 39: 1101–1111. – 
		  {" "}<a href="http://onlinelibrary.wiley.com/doi/10.1111/j.1365-2699.2011.02664.x/abstract">fulltext in Wiley Online Library</a>
        </li>
        
        <li>
          <b>Lososová Z.</b>, <b>Chytrý M.</b>, <b>Tichý L.</b>, <b>Danihelka J.</b>, Fajmon K., <b>Hájek O.</b>, <b>Kintrová K.</b>, Kühn I.,
          <b> Láníková D.</b>, <b>Otýpková Z.</b> &amp; <b>Řehořek V.</b> (2012): Native and alien floras in urban
          habitats: a comparison across 32 cities of central Europe. <i>Global Ecology and Biogeography </i>
          21: 545–555. – 
		  {" "}<a href="http://onlinelibrary.wiley.com/doi/10.1111/j.1466-8238.2011.00704.x/abstract">fulltext in Wiley Online Library</a>
        </li>
        <li>
          <b>Lososová Z.</b>, <b>Chytrý M.</b>, <b>Tichý L.</b>, <b>Danihelka J.</b>, Fajmon K., <b>Hájek O.</b>, <b>Kintrová K.</b>, Láníková
          D., <b>Otýpková Z.</b> &amp; <b>Řehořek V.</b> (2012): Biotic homogenization of Central European urban
          floras depends on residence time of alien species and habitat types. <i>Biological
          Conservation</i> 145: 179–184.
        </li>
        
        <h4>Disertace</h4>
        <li>
          <b>Čeplová N.</b> (2016): Diversity of European urban vegetation. Thesis. Ms. Masaryk University,
          Brno.
        </li>
        <h4>Popularizační články</h4>
        <li>
          <b>Lososová Z.</b> (2018): Diverzita rostlinných společenstev evropských měst. <i>Zprávy České
          botanické společnosti</i> 53: 207–216.
        </li>
<li>
          Pergl J., <b>Lososová Z.</b>, Sádlo J. &amp; Štajerová K. (2018): Rostlinné invaze na
          antropogenních stanovištích. <i>Živa</i> 5: 233–234.
        </li>   
 <li>
 <b>Lososová Z.</b>, Kovář P., Medvecká J., Mudrák O., Pergl J. & Prach K. (2018): Úvod: Flóra a vegetace antropogenních stanovišť. <i>Zprávy České botanické společnosti</i> 53: 199–205.
</li>

<li>
          <b>Čeplová N.</b> &amp; <b>Kalusová V.</b> (2016): Jak velikost města ovlivňuje druhové složení
          vegetace? <i>Fórum ochrany přírody</i> 3/4: 30–34.
        </li>
       <li>
          <b>Lososová Z.</b>, Šumberová K., <b>Tichý L.</b>, <b>Vymazalová M.</b> &amp; <b>Danihelka J.</b> (2015): Květena Brna
          – současný stav poznání. <i>Živa</i> 2015/6: 289–292.
        </li>
        <li>
          <b>Lososová Z.</b> &amp; <b>Danihelka J.</b> (2014): Proč je flóra velkoměst pestrá. <i>Živa</i> 2014/6:
          278–281.
        </li>
      </div>
    </div>
  )
};
